import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FileDown, Printer, ExternalLink, CheckCircle2, Circle, Clock, Lock, Mail, Archive, ClipboardCheck, RefreshCw } from "lucide-react";
import WorkflowDiagram from "@/components/WorkflowDiagram";

const statusIcon = (s) =>
  s === "complete" ? <CheckCircle2 className="w-4 h-4 text-[#22C55E]" /> :
  s === "in_progress" ? <Clock className="w-4 h-4 text-[#F59E0B]" /> :
  s === "locked" ? <Lock className="w-4 h-4 text-slate-500" /> :
  <Circle className="w-4 h-4 text-slate-500" />;

const label = (s) => (s || "not_started").replace(/_/g, " ");

export default function Workspace() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => { api.get(`/projects/${id}`).then(({ data }) => setP(data)); }, [id]);
  if (!p) return null;

  const spec = p.specification || {};
  const completeness = (() => {
    const keys = ["functional_objective", "dimensional_requirements", "material_requirements", "loading_conditions", "environmental_conditions", "manufacturing_constraints", "safety_considerations"];
    const filled = keys.filter((k) => (spec[k] || "").trim().length > 20).length;
    return Math.round((filled / keys.length) * 100);
  })();

  // Downstream stages are locked until the specification is approved.
  const downstream = (key, stored) => (p.specification_approved ? (stored || "not_started") : "locked");

  const outstanding = (spec.missing_information || []).concat(spec.assumptions_requiring_confirmation || []);

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/report.pdf`;

  return (
    <div className="space-y-10">
      <div className="flex flex-wrap gap-4 justify-between items-end">
        <div>
          <div className="font-tech mb-2">Project Workspace</div>
          <h1 className="text-3xl lg:text-4xl font-semibold text-white">{p.intent?.project_name}</h1>
          <div className="mt-2 text-slate-500 font-mono text-xs">Project ID · {p.id}</div>
        </div>
        <div className="flex gap-3">
          <Link to={`/projects/${p.id}/report`} className="btn-ghost text-sm" data-testid="ws-print-view"><Printer className="w-4 h-4" /> Print View</Link>
          <a href={pdfUrl} className="btn-primary text-sm" data-testid="ws-download-pdf"><FileDown className="w-4 h-4" /> Download PDF</a>
        </div>
      </div>

      <WorkflowDiagram activeId={p.specification_approved && p.geometry_status && p.geometry_status !== "locked" && p.geometry_status !== "not_started" ? "geometry" : "specification"} />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="panel-elev tick-brackets p-6 lg:col-span-2 space-y-5">
          <div>
            <div className="font-tech mb-1">Specification Completeness</div>
            <div className="flex items-end gap-4">
              <div className="metric-value">{completeness}%</div>
              <div className="flex-1 h-2 bg-[#0B111C] border border-[#1E2F48] mb-2">
                <div className="h-full bg-gradient-to-r from-[#0066FF] to-[#00D2FF]" style={{ width: `${completeness}%` }} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              ["geometry_status", "Geometry"],
              ["material_status", "Material selection"],
              ["simulation_status", "Simulation"],
              ["validation_status", "Validation"],
              ["manufacturing_output_status", "Manufacturing outputs"],
            ].map(([k, l]) => {
              const effective = downstream(k, p[k]);
              return (
                <div key={k} className="panel p-4 flex items-center justify-between" data-testid={`ws-status-${k}`}>
                  <div>
                    <div className="font-tech text-[10px]">{l}</div>
                    <div className="text-white text-sm mt-1 capitalize">{label(effective)}</div>
                  </div>
                  {statusIcon(effective)}
                </div>
              );
            })}
          </div>
        </div>

        <div className="panel-elev p-6 space-y-4">
          <div className="font-tech">Outstanding Questions</div>
          {outstanding.length === 0 && <div className="text-slate-500 text-sm">None recorded.</div>}
          <ul className="space-y-2 text-sm text-slate-300">
            {outstanding.map((q, i) => (
              <li key={i} className="flex gap-2"><span className="text-[#C9A961] font-mono">•</span> {q}</li>
            ))}
          </ul>
          <div className="dim-line" />
          <div className="font-tech">Revision History</div>
          <ul className="space-y-2 text-xs font-mono text-slate-400">
            <li data-testid="ws-revision-line">rev {p.revision} · updated {new Date(p.updated_at).toLocaleDateString()}</li>
            {(p.revision_history || []).slice(-4).reverse().map((r, i) => (
              <li key={i}>rev {r.revision} · {new Date(r.at).toLocaleString()} · {r.note}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="panel-elev p-6">
        <div className="font-tech mb-3">Approved Specification</div>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          {Object.entries({
            "Functional objective": spec.functional_objective,
            "Dimensional requirements": spec.dimensional_requirements,
            "Material requirements": spec.material_requirements,
            "Loading conditions": spec.loading_conditions,
            "Environmental conditions": spec.environmental_conditions,
            "Manufacturing constraints": spec.manufacturing_constraints,
            "Safety considerations": spec.safety_considerations,
          }).map(([k, v]) => (
            <div key={k} className="panel p-4">
              <div className="font-tech mb-2">{k}</div>
              <pre className="whitespace-pre-wrap font-mono text-xs text-slate-200 leading-relaxed">{v || "Not provided"}</pre>
            </div>
          ))}
        </div>
      </div>

      <div className="panel-elev p-6 flex items-center gap-3">
        <ExternalLink className="w-4 h-4 text-[#C9A961]" />
        <div className="text-slate-300 text-sm">
          Professional engineering review remains required before real-world fabrication or safety-critical use.
        </div>
      </div>

      <FreeDemoActions projectId={p.id} projectName={p.intent?.project_name} approved={!!p.specification_approved} />
    </div>
  );
}

function FreeDemoActions({ projectId, projectName, approved }) {
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [sending, setSending] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [mfgSubmitting, setMfgSubmitting] = useState(false);
  const [mfgConfirmed, setMfgConfirmed] = useState(false);
  const [grantExpiresAt, setGrantExpiresAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!grantExpiresAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [grantExpiresAt]);

  const grantActive = grantExpiresAt && grantExpiresAt > now;
  const remaining = grantActive ? Math.max(0, Math.floor((grantExpiresAt - now) / 1000)) : 0;
  const pkgUrl = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/portable.zip`;

  const requestCode = async () => {
    setRequesting(true);
    try {
      await api.post("/entitlements/request-delivery-code", { project_id: projectId });
      toast.success("If a delivery step is required, we've emailed a new code.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        toast.error(e?.response?.data?.detail || "We couldn't send your delivery email right now. Please try again later.");
      } else {
        toast.error(e?.response?.data?.detail || "Please try again shortly.");
      }
    }
    finally { setRequesting(false); }
  };

  const redeem = async () => {
    if (!/^\d{6}$/.test(code.trim())) return toast.error("Enter the six-digit delivery code emailed to you.");
    setRedeeming(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/redeem-delivery-code`,
        { project_id: projectId, code: code.trim() },
        { withCredentials: true });
      const expiresAt = Date.now() + (data.expires_in_seconds || 1800) * 1000;
      setGrantExpiresAt(expiresAt);
      setCode("");
      toast.success("Delivery authorization active for 30 minutes.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Redemption failed."); }
    finally { setRedeeming(false); }
  };

  const sendPackage = async () => {
    setSending(true);
    try {
      await api.post(`/projects/${projectId}/email-package`, {}, { withCredentials: true });
      toast.success("Package emailed to your verified address.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        toast.error("Delivery authorization required. Please redeem your delivery code.");
        setGrantExpiresAt(null);
      } else {
        toast.error(e?.response?.data?.detail || "Delivery failed.");
      }
    }
    finally { setSending(false); }
  };

  const submitMfg = async () => {
    if (!mfgConfirmed) return toast.error("Please confirm the review acknowledgement.");
    setMfgSubmitting(true);
    try {
      await api.post(`/projects/${projectId}/manufacturing-review`, { project_id: projectId, consent_acknowledged: true });
      toast.success("Submitted for manufacturing review.");
      setMfgConfirmed(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed."); }
    finally { setMfgSubmitting(false); }
  };

  const downloadZip = () => {
    // Sends cookies as same-origin request. Browser initiates the download.
    window.location.href = pkgUrl;
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="free-demo-actions">
      <div className="panel-elev p-5 space-y-3">
        <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#00D2FF]" /><span className="font-tech">Redeem Delivery Code</span></div>
        <p className="text-slate-400 text-xs">Enter the six-digit delivery code emailed to your verified address to unlock both actions below for 30 minutes.</p>
        <input className="input-cad font-mono text-center tracking-[0.4em]" maxLength={6}
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="ws-delivery-code" placeholder="000000" disabled={grantActive} />
        <div className="flex gap-2">
          <button onClick={redeem} disabled={redeeming || grantActive} className="btn-primary text-sm flex-1 justify-center" data-testid="ws-redeem-code">
            {redeeming ? "Redeeming…" : (grantActive ? "Active" : "Redeem")}
          </button>
          <button onClick={requestCode} disabled={requesting} className="btn-ghost text-xs" data-testid="ws-request-code">
            <RefreshCw className="w-3 h-3" /> New code
          </button>
        </div>
        {grantActive && (
          <div className="text-[11px] font-mono text-[#22C55E]" data-testid="ws-grant-timer">
            Delivery grant active — {Math.floor(remaining/60)}m {String(remaining%60).padStart(2,"0")}s remaining · max 3 downloads
          </div>
        )}
      </div>

      <div className="panel-elev p-5 space-y-3">
        <div className="flex items-center gap-2"><Archive className="w-4 h-4 text-[#C9A961]" /><span className="font-tech">Email + Portable Package</span></div>
        <p className="text-slate-400 text-xs">
          Download this package locally or save it to a connected USB drive using your browser's normal download controls.
        </p>
        <button onClick={sendPackage} disabled={sending || !grantActive} className="btn-primary text-sm w-full justify-center" data-testid="ws-email-package">
          {sending ? "Sending…" : "Email the package"}
        </button>
        <button onClick={downloadZip} disabled={!grantActive} className="btn-copper text-sm w-full justify-center" data-testid="ws-portable-zip">
          <FileDown className="w-4 h-4" /> Download ZIP
        </button>
        <p className="text-slate-500 text-[10px] leading-relaxed">
          Contains only genuine outputs. No fabricated CAD, STEP, STL, simulation, or manufacturing files.
        </p>
      </div>

      <div className="panel-elev p-5 space-y-3">
        <div className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-[#22C55E]" /><span className="font-tech">Submit for Manufacturing Review</span></div>
        {!approved ? (
          <p className="text-slate-500 text-xs">Available after the specification is approved.</p>
        ) : (
          <>
            <label className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer">
              <input type="checkbox" className="mt-0.5 accent-[#22C55E]" checked={mfgConfirmed}
                onChange={(e) => setMfgConfirmed(e.target.checked)} data-testid="ws-mfg-ack" />
              <span>I understand this submits my project for feasibility and manufacturing review. It does not guarantee manufacturability, certification, pricing, acceptance, or production.</span>
            </label>
            <button onClick={submitMfg} disabled={mfgSubmitting || !mfgConfirmed} className="btn-primary text-sm w-full justify-center" data-testid="ws-mfg-submit">
              {mfgSubmitting ? "Submitting…" : "Submit for review"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
